import { etagFromRow, get, post, request, seg } from '../client';
import { session } from '../session';
import type { ManagedDevice } from '../types/admin';
import type { ListParams, Page, PatientRef, PolicyDecision, PurposeOfUse } from '../types/common';
import type {
  AmbulatoryDashboard, AuditEvent, AuditSearchParams, AuditVerifyResult, BackEntryRequest, BreakGlassReviewItem, BreakGlassReviewRequest, ComplianceReport,
  BackEntryResult, CreateDowntimePacketRequest, CreateExportRequest, DowntimePacketDownload, CreateLegalHoldRequest, DataQualityException, Disclosure, DisclosureSearchParams, DowntimePacket, ExportContent, ExportDownload,
  ExportRequest, LegalHold, MetricDefinition, SafetyReportCode, SafetyReportRequest,
} from '../types/audit';

const C = { purpose: 'COMPLIANCE' as const };

/* ------------------------------------------------------------------------------------------------
 * Normalizers: backend DTOs / raw rows → UI types. Each one passes through objects that are already
 * UI-shaped (the in-browser mock) so both transports share the feature code.
 * ---------------------------------------------------------------------------------------------- */

type Raw = Record<string, unknown>;
const isObj = (v: unknown): v is Raw => !!v && typeof v === 'object' && !Array.isArray(v);
const s = (v: unknown): string | null => (typeof v === 'string' ? v : v == null ? null : String(v));
const PURPOSES: PurposeOfUse[] = ['TREATMENT', 'PAYMENT', 'OPERATIONS', 'EMERGENCY', 'PATIENT_REQUEST', 'COMPLIANCE'];

/** A patient reference built from an id only (the audit ledger stores ids, not names). Never shows the UUID as a name. */
export function auditPatientFromId(id: string): PatientRef {
  return { id, display: 'Patient record', mrn: '', dob: '', age: '' };
}

function toPatientRef(v: unknown, id: unknown): PatientRef | null {
  if (isObj(v) && typeof v.id === 'string') {
    return { id: v.id, display: s(v.display) ?? 'Patient record', mrn: s(v.mrn) ?? '', dob: s(v.dob) ?? '', age: s(v.age) ?? '', ...(v.sex ? { sex: s(v.sex)! } : {}) };
  }
  return typeof id === 'string' && id ? auditPatientFromId(id) : null;
}

/** allow|deny from the backend outcome vocabulary (success, allowed, denied, deny, failure, forbidden, error …). */
export function auditOutcome(v: unknown): 'allow' | 'deny' {
  const o = String(v ?? '').toLowerCase();
  return /^(deny|denied|forbidden|refused|blocked|failure|failed|error|rejected)$/.test(o) ? 'deny' : 'allow';
}

/** Normalize one audit event (raw `audit.audit_event` row, governed DTO, or mock event). */
export function normalizeAuditEvent(raw: unknown): AuditEvent {
  const r = (isObj(raw) ? raw : {}) as Raw;
  const ev = (isObj(r.event) ? r.event : {}) as Raw;
  const actorObj = isObj(r.actor) ? r.actor : null;
  const actorId = s(actorObj?.id) ?? s(r.actorId) ?? s(ev.actorId) ?? '';
  const role = s(actorObj?.role) ?? s(r.actorRole) ?? s(r.roleId) ?? s(ev.roleId) ?? undefined;
  const actorDisplay = s(actorObj?.display) ?? s(r.actorDisplay) ?? (actorId ? 'Staff member' : 'System');
  const outcomeRaw = s(r.outcome) ?? s(ev.outcome) ?? s(ev.decision);
  const outcome = auditOutcome(outcomeRaw);
  const purposeRaw = s(r.purpose) ?? s(ev.purpose) ?? 'OPERATIONS';
  const purpose = (PURPOSES.includes(purposeRaw as PurposeOfUse) ? purposeRaw : purposeRaw) as PurposeOfUse;
  const resObj = isObj(r.resource) ? r.resource : null;
  const resource = resObj
    ? { type: s(resObj.type) ?? 'resource', id: s(resObj.id) ?? '', ...(resObj.display ? { display: s(resObj.display)! } : {}) }
    : { type: s(r.resourceType) ?? s(ev.resourceType) ?? 'resource', id: s(r.resourceId) ?? s(ev.resourceId) ?? '' };
  const breakGlass =
    typeof r.breakGlass === 'boolean' ? r.breakGlass : !!(r.breakGlassId ?? ev.breakGlassId ?? ev.breakGlass) || purpose === 'EMERGENCY';
  const reasons = Array.isArray(ev.reasons) ? ev.reasons.map(String) : Array.isArray(r.reasons) ? (r.reasons as unknown[]).map(String) : [];
  const reason = s(r.reason) ?? s(r.reasonCode) ?? s(ev.reasonCode) ?? s(ev.reason) ?? (reasons.length ? reasons.join(', ') : null);
  const action = s(r.action) ?? s(ev.action) ?? 'unknown';
  let policy: PolicyDecision;
  if (isObj(r.policy) && Array.isArray(r.policy.inputs)) {
    policy = r.policy as unknown as PolicyDecision;
  } else {
    const pol = isObj(r.policy) ? r.policy : {};
    const polReasons = Array.isArray(pol.reasons) ? pol.reasons.map(String) : reasons;
    const decisionRaw = s(pol.decision) ?? s(ev.decision);
    const decision: PolicyDecision['decision'] = decisionRaw === 'conditional' ? 'conditional' : decisionRaw ? (auditOutcome(decisionRaw) === 'deny' ? 'deny' : 'allow') : outcome;
    const obligations = Array.isArray(pol.obligations) ? pol.obligations.map(String) : Array.isArray(ev.obligations) ? (ev.obligations as unknown[]).map(String) : undefined;
    policy = {
      decision,
      action,
      inputs: [
        { label: 'Purpose of use', value: purposeRaw, result: 'na' },
        ...(s(ev.assurance) ? [{ label: 'Sign-in assurance', value: s(ev.assurance)!, result: 'na' as const }] : []),
        ...(s(ev.dataClassification) ? [{ label: 'Data classification', value: s(ev.dataClassification)!, result: 'na' as const }] : []),
        ...polReasons.map((x) => ({ label: 'Policy reason', value: x, result: decision === 'deny' ? ('fail' as const) : ('na' as const) })),
      ],
      ...(obligations ? { obligations } : {}),
      policyVersion: s(pol.policyVersion) ?? s(r.policyVersion) ?? s(ev.policyVersion) ?? 'not recorded',
      ...(s(pol.correlationId) ?? s(ev.correlationId) ? { correlationId: (s(pol.correlationId) ?? s(ev.correlationId))! } : {}),
    };
  }
  return {
    id: s(r.id) ?? '',
    at: s(r.at) ?? s(r.recordedAt) ?? s(r.createdAt) ?? s(ev.recordedAt) ?? '',
    actor: { id: actorId, display: actorDisplay, ...(role ? { role } : {}) },
    action,
    outcome,
    rawOutcome: outcomeRaw,
    patient: toPatientRef(r.patient, r.patientId ?? ev.patientId),
    resource,
    purpose,
    breakGlass,
    reason,
    requestId: s(r.requestId) ?? s(ev.requestId) ?? '',
    ip: s(r.ip) ?? s(ev.ip),
    policy,
    ingestSeq: s(r.ingestSeq ?? r.ingest_seq) ?? undefined,
    chainStatus: r.chainStatus === 'pending' || r.chainStatus === 'chained' ? r.chainStatus : undefined,
    hash: s(r.hash) ?? '',
    prevHash: s(r.prevHash) ?? '',
    sequence: (r.sequence as string | number | undefined) ?? (ev.sequence as number | undefined) ?? null,
  };
}

/** Outcome / emergency filters re-applied to each returned page (defensive; the backend filters too). */
export function auditFilterLocally(items: AuditEvent[], p: Pick<AuditSearchParams, 'outcome' | 'breakGlass'>): AuditEvent[] {
  return items.filter((e) => (!p.outcome || e.outcome === p.outcome) && (!p.breakGlass || e.breakGlass));
}

/** Newest first, stable (the backend pages in id order, not time order). */
export const sortAuditNewestFirst = (items: AuditEvent[]) =>
  [...items].sort((a, b) => (a.at === b.at ? (a.id < b.id ? 1 : -1) : a.at < b.at ? 1 : -1));

/** Normalize GET /audit-events/verify: backend {valid,eventCount,invalidSequences} or the governed DTO. */
export function normalizeAuditVerify(raw: unknown, receivedAt = new Date().toISOString()): AuditVerifyResult {
  const r = (isObj(raw) ? raw : {}) as Raw;
  if (r.status === 'verified' || r.status === 'broken') {
    const v = r as unknown as AuditVerifyResult;
    return { ...v, checkedEvents: Number(v.checkedEvents ?? 0), brokenAt: v.brokenAt ?? null, verifiedAt: v.verifiedAt ?? receivedAt };
  }
  const invalid = Array.isArray(r.invalidSequences) ? (r.invalidSequences as (string | number)[]) : [];
  const valid = r.valid === true && invalid.length === 0;
  return {
    status: valid ? 'verified' : 'broken',
    checkedEvents: Number(r.eventCount ?? r.checkedEvents ?? 0),
    firstEventAt: s(r.firstEventAt),
    lastEventAt: s(r.lastEventAt),
    headHash: s(r.headHash),
    brokenAt: valid ? null : { eventId: invalid.length ? `sequence ${invalid[0]}` : 'unknown', at: null },
    invalidSequences: invalid,
    verifiedAt: s(r.verifiedAt) ?? receivedAt,
  };
}

const MS_HOUR = 3_600_000;
/** Normalize a break-glass grant listed for review (raw `mpi.break_glass_grant` row or the UI/mock shape). */
export function normalizeBreakGlassReview(raw: unknown, now = Date.now()): BreakGlassReviewItem {
  const r = (isObj(raw) ? raw : {}) as Raw;
  const uiShaped = isObj(r.actor) && isObj(r.review);
  const patient = toPatientRef(r.patient, r.patientId);
  if (uiShaped) {
    const g = r as unknown as BreakGlassReviewItem;
    return { ...g, patient: g.patient ?? patient, relatedEvents: g.relatedEvents ?? null, detailKnown: g.detailKnown ?? true };
  }
  const startedAt = s(r.startedAt) ?? s(r.createdAt) ?? '';
  const expiresAt = s(r.expiresAt) ?? new Date(new Date(startedAt).getTime() + MS_HOUR).toISOString();
  const endedAt = s(r.endedAt);
  const status: BreakGlassReviewItem['status'] = endedAt ? 'ended' : new Date(expiresAt).getTime() <= now ? 'expired' : 'active';
  const rs = s(r.reviewStatus) ?? 'pending';
  const reviewStatus = (rs === 'needs_follow_up' ? 'needs_followup' : rs) as BreakGlassReviewItem['review']['status'];
  const actor = isObj(r.actor) ? { id: s(r.actor.id) ?? '', display: s(r.actor.display) ?? 'Staff member' } : { id: s(r.actorId) ?? '', display: s(r.actorDisplay) ?? 'Staff member' };
  const scope = Array.isArray(r.scope) ? r.scope.map(String) : [];
  return {
    id: s(r.id) ?? '',
    patientId: s(r.patientId) ?? patient?.id ?? '',
    patientDisplay: patient?.display ?? 'Patient record',
    patient,
    actor,
    reason: (s(r.reasonCode) ?? s(r.reason) ?? 'emergency') as BreakGlassReviewItem['reason'],
    justification: s(r.justification) ?? '',
    purpose: (s(r.purpose) ?? 'EMERGENCY') as PurposeOfUse,
    startedAt,
    expiresAt,
    endedAt,
    status,
    visible: scope,
    blocked: [],
    accessedResources: typeof r.accessedResources === 'number' ? r.accessedResources : 0,
    relatedEvents: typeof r.relatedEvents === 'number' ? r.relatedEvents : typeof r.relatedEventCount === 'number' ? r.relatedEventCount : null,
    review: {
      status: reviewStatus,
      ...(s(r.reviewerDisplay) ?? s(r.reviewerId) ? { by: s(r.reviewerDisplay) ?? 'Another privacy officer' } : {}),
      ...(s(r.reviewedAt) ? { at: s(r.reviewedAt)! } : {}),
      ...(s(r.reviewReasonCode) ? { note: s(r.reviewReasonCode)! } : {}),
    },
    reviewReasonCode: s(r.reviewReasonCode),
    ...(r.rowVersion != null ? { rowVersion: String(r.rowVersion) } : {}),
    detailKnown: false,
  };
}

/** Bring an ExportRequest DTO to the UI shape (null dates → undefined; pass-through otherwise). */
export function normalizeExport(raw: unknown): ExportRequest {
  const e = raw as ExportRequest;
  return { ...e, scope: { ...e.scope, from: e.scope?.from ?? undefined, to: e.scope?.to ?? undefined, categories: e.scope?.categories ?? [] } };
}

const mapPage = <A, B>(p: Page<A>, f: (a: A) => B): Page<B> => ({ ...p, items: (p.items ?? []).map(f) });
/** Newest first within a page (the backend pages in id order, not time order). */
const newestFirst = <T extends { id: string }>(p: Page<T>, key: (t: T) => string | null | undefined): Page<T> => ({
  ...p,
  items: [...p.items].sort((a, b) => {
    const ka = key(a) ?? '';
    const kb = key(b) ?? '';
    return ka === kb ? (a.id < b.id ? 1 : -1) : ka < kb ? 1 : -1;
  }),
});

/** Start / end of a local calendar day (YYYY-MM-DD) as RFC 3339 instants. Pass-through for full datetimes. */
export function dayStartIso(d: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`).toISOString() : d;
}
export function dayEndIso(d: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T23:59:59.999`).toISOString() : d;
}

export const auditApi = {
  /**
   * Backend filters: patientId, actorId, action, from, to, outcome (allow|deny), breakGlass (true). `breakGlass=false`
   * is never sent (it means "no filter"). The page is also filtered locally so a server that ignores a filter can
   * never show non-matching rows, and sorted newest first (the server pages in id order).
   */
  search: async (p: AuditSearchParams): Promise<Page<AuditEvent>> => {
    const { outcome, breakGlass, ...q } = p;
    const page = await get<Page<unknown>>('/audit-events', { ...q, outcome, breakGlass: breakGlass ? true : undefined }, C);
    return { ...page, items: sortAuditNewestFirst(auditFilterLocally((page.items ?? []).map(normalizeAuditEvent), { outcome, breakGlass })) };
  },
  verify: async () => normalizeAuditVerify(await get<unknown>('/audit-events/verify', undefined, C)),
  /** Action vocabulary recorded in this tenant's ledger (GET /audit-events/actions). */
  actions: () => get<Page<{ id: string; display: string }>>('/audit-events/actions', undefined, C),
  /**
   * The backend list has no status filter yet (always pending). `status` is still sent (forward compatible) and
   * the result is filtered here so the Reviewed tab never shows pending grants.
   */
  breakGlassReviews: async (p: ListParams & { status?: 'pending' | 'reviewed' } = {}) => {
    const page = newestFirst(mapPage(await get<Page<unknown>>('/break-glass/reviews', { ...p }, C), (g) => normalizeBreakGlassReview(g)), (g) => g.startedAt);
    if (!p.status) return page;
    return { ...page, items: page.items.filter((g) => (p.status === 'pending' ? g.review.status === 'pending' : g.review.status !== 'pending')) };
  },
  /** If-Match is required: pass `W/"<rowVersion>"` from the list item. */
  reviewBreakGlass: async (id: string, body: BreakGlassReviewRequest, etag: string | null) =>
    normalizeBreakGlassReview(await post<unknown>(`/break-glass/${seg(id)}:review`, body, { ...C, ifMatch: etag, idempotent: true })),
  disclosures: async (p: DisclosureSearchParams = {}) =>
    newestFirst(await get<Page<Disclosure>>('/disclosures', { ...p, from: p.from ? dayStartIso(p.from) : undefined, to: p.to ? dayEndIso(p.to) : undefined }, C), (d) => d.disclosedAt),
};

export const exportsApi = {
  list: async (p: ListParams & { status?: string } = {}) => newestFirst(mapPage(await get<Page<ExportRequest>>('/exports', { ...p }), normalizeExport), (e) => e.createdAt),
  /** The body purpose must equal the active purpose of use, so the request is sent under that purpose. */
  create: async (body: CreateExportRequest) => normalizeExport(await post<ExportRequest>('/exports', body, { idempotent: true, purpose: body.purpose })),
  /** Approver must differ from requester; step-up and If-Match required. */
  approve: async (id: string, etag: string | null = null) =>
    normalizeExport(await post<ExportRequest>(`/exports/${seg(id)}:approve`, {}, { ifMatch: etag, idempotent: true, stepUpLabel: 'Approve data export' })),
  revoke: async (id: string, reason: string, etag: string | null = null) =>
    normalizeExport(await post<ExportRequest>(`/exports/${seg(id)}:revoke`, { reason }, { ifMatch: etag, idempotent: true })),
  /** `purpose` must be the export's approved purpose; it is also sent as X-Purpose-Of-Use. */
  download: (id: string, purpose: PurposeOfUse) => get<ExportDownload>(`/exports/${seg(id)}/download`, { purpose }, { purpose }),
  /**
   * Fetch the watermarked file behind a download URL with the session's Bearer token and the approved purpose
   * (a plain <a href> cannot send them). Mock data: URLs are decoded locally.
   */
  content: async (url: string, purpose: PurposeOfUse): Promise<ExportContent> => {
    if (url.startsWith('data:')) {
      const comma = url.indexOf(',');
      const meta = url.slice(5, comma);
      const body = url.slice(comma + 1);
      const content = meta.includes(';base64') ? atob(body) : decodeURIComponent(body);
      return { filename: '', mediaType: meta.split(';')[0] || 'application/octet-stream', content, checksum: '', watermark: '' };
    }
    const path = url.replace(/^(https?:\/\/[^/]+)?\/api\/v1/, '');
    return (await request<ExportContent>('GET', path, { purpose })).data;
  },
};

export const legalHoldsApi = {
  /** Omit status for "all" (the backend validates active|released). */
  list: (p: ListParams & { status?: 'active' | 'released' | 'all' } = {}) => {
    const { status, ...rest } = p;
    return get<Page<LegalHold>>('/legal-holds', { ...rest, status: status === 'all' ? undefined : status }, C).then((pg) => newestFirst(pg, (h) => h.createdAt));
  },
  create: (body: CreateLegalHoldRequest) => post<LegalHold>('/legal-holds', body, { ...C, idempotent: true }),
  /** Step-up and If-Match required. */
  release: (id: string, reason: string, etag: string | null = null) =>
    post<LegalHold>(`/legal-holds/${seg(id)}:release`, { reason }, { ...C, ifMatch: etag, idempotent: true, stepUpLabel: 'Release legal hold' }),
};

/** SAFE-001 coded descriptions (the backend accepts these codes only — no free text). */
export const SAFETY_REPORT_CODES: { value: SafetyReportCode; label: string }[] = [
  { value: 'WRONG_PATIENT', label: 'Wrong patient (record, label or document)' },
  { value: 'WRONG_MEDICATION', label: 'Wrong medication, dose or route' },
  { value: 'MISSED_FOLLOW_UP', label: 'Missed or late follow-up' },
  { value: 'PRIVACY_EVENT', label: 'Privacy event (shown or sent to the wrong person)' },
  { value: 'WORKFLOW_HAZARD', label: 'Workflow or system hazard' },
  { value: 'OTHER_REVIEW_REQUIRED', label: 'Something else — the safety team will follow up' },
];

/**
 * Screen code for a safety report's context.route (/^[A-Za-z0-9_.:-]{1,120}$/): "/admin/downtime" → "admin.downtime".
 * Id-like segments (UUIDs, long digits, mock ids) are dropped so no identifiers leave in the report.
 */
export function safetyRouteCode(pathname: string): string {
  const parts = pathname
    .split('/')
    .filter(Boolean)
    .filter((x) => !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(x) && !/^\d{3,}$/.test(x) && !/^(p|enc|u|acct|cmp)-/.test(x))
    .map((x) => x.replace(/[^A-Za-z0-9_-]/g, '_'));
  const code = (parts.join('.') || 'home').slice(0, 120);
  return code;
}

/** W/"<rowVersion>" for a compliance aggregate listed by the backend (null when unknown, e.g. mock rows). */
export const complianceEtag = (row: { rowVersion?: string | number | null; version?: number } | null | undefined) =>
  etagFromRow(row?.rowVersion ?? row?.version ?? null);

export const reportsApi = {
  compliance: (from: string, to: string) => get<ComplianceReport>('/reports/compliance', { from, to }, C),
  /** Uses the session's purpose of use (R-057: never forced to OPERATIONS). */
  ambulatoryDashboard: (p: { locationId?: string; date?: string } = {}) => get<AmbulatoryDashboard>('/dashboards/ambulatory', { ...p }),
  metricCatalog: () => get<Page<MetricDefinition>>('/metric-catalog'),
  dataQualityExceptions: (p: ListParams & { status?: string } = {}) => get<Page<DataQualityException>>('/data-quality-exceptions', { ...p }),
  /** If-Match required: pass `W/"<rowVersion>"` from the listed exception (complianceEtag(x)). */
  resolveDataQualityException: (id: string, resolution: string, etag: string | null = null) =>
    post<DataQualityException>(`/data-quality-exceptions/${seg(id)}:resolve`, { resolution }, { ifMatch: etag, idempotent: true }),
};

/* ---------------- Managed devices (downtime packets; admin agent) ----------------
 * core-api requires an APPROVED managed device bound to the signed-in actor for every packet call:
 * headers X-Managed-Device-Id + X-Device-Token. Enrollment: POST /managed-devices {siteId} (step-up) returns the
 * device-bound token ONCE → a different person with managed_device.manage approves (If-Match, step-up, idempotent).
 * The token is kept in this browser's localStorage, scoped to tenant + actor; it never leaves the device except as the header.
 */
export interface StoredDevice {
  id: string;
  token: string;
  siteId: string;
  expiresAt: string;
  status?: string;
}
const deviceKey = () => {
  const s = session.get();
  return s.tenantId && s.user ? `etnas.managedDevice.${s.tenantId}.${s.user.id}` : null;
};
export const deviceStore = {
  get(): StoredDevice | null {
    const k = deviceKey();
    if (!k) return null;
    try {
      const v = JSON.parse(globalThis.localStorage?.getItem(k) ?? 'null') as StoredDevice | null;
      return v && v.id && v.token ? v : null;
    } catch {
      return null;
    }
  },
  set(d: StoredDevice) {
    const k = deviceKey();
    if (k) globalThis.localStorage?.setItem(k, JSON.stringify(d));
  },
  clear() {
    const k = deviceKey();
    if (k) globalThis.localStorage?.removeItem(k);
  },
};
/** Headers that bind a packet call to this enrolled device (empty when not enrolled). */
export const deviceHeaders = (): Record<string, string> => {
  const d = deviceStore.get();
  return d ? { 'X-Managed-Device-Id': d.id, 'X-Device-Token': d.token } : {};
};
const devTag = (d: { rowVersion?: number | string | null; etag?: string | null }) => d.etag ?? etagFromRow(d.rowVersion);

/* Device key pair: RSA-OAEP-256 (3072-bit). The private key is generated NON-extractable and kept in IndexedDB
 * (in-memory fallback where IndexedDB is unavailable); only the SPKI public key is sent to core-api. */
const memKeys = new Map<string, CryptoKey>();
const IDB = 'etnas-device-keys';
function idb(): Promise<IDBDatabase | null> {
  if (!globalThis.indexedDB) return Promise.resolve(null);
  return new Promise((resolve) => {
    const r = globalThis.indexedDB.open(IDB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore('keys');
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => resolve(null);
  });
}
async function saveDeviceKey(deviceId: string, key: CryptoKey) {
  memKeys.set(deviceId, key);
  const db = await idb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction('keys', 'readwrite');
    tx.objectStore('keys').put(key, deviceId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}
async function loadDeviceKey(deviceId: string): Promise<CryptoKey | null> {
  if (memKeys.has(deviceId)) return memKeys.get(deviceId)!;
  const db = await idb();
  if (!db) return null;
  return new Promise((resolve) => {
    const r = db.transaction('keys').objectStore('keys').get(deviceId);
    r.onsuccess = () => resolve((r.result as CryptoKey | undefined) ?? null);
    r.onerror = () => resolve(null);
  });
}
const b64ToBytes = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const bytesToB64 = (b: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(b)));
const subtle = () => {
  const s = globalThis.crypto?.subtle;
  if (!s) throw new Error('This browser cannot create a device key (Web Crypto unavailable).');
  return s;
};
async function newDeviceKeyPair() {
  const pair = (await subtle().generateKey({ name: 'RSA-OAEP', modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' }, false, ['encrypt', 'decrypt'])) as CryptoKeyPair;
  const spki = bytesToB64(await subtle().exportKey('spki', pair.publicKey));
  const pem = `-----BEGIN PUBLIC KEY-----\n${spki.match(/.{1,64}/g)!.join('\n')}\n-----END PUBLIC KEY-----\n`;
  return { pair, pem };
}
async function unwrap(privateKey: CryptoKey, wrappedB64: string) {
  return subtle().decrypt({ name: 'RSA-OAEP' }, privateKey, b64ToBytes(wrappedB64));
}

/** Decrypted downtime packet (synthetic; AES-256-GCM key wrapped to this device's public key). */
export interface OpenedPacket {
  packetId: string;
  siteId: string;
  generatedAt: string;
  expiresAt: string;
  watermark?: string;
  roster: { appointmentId: string; patientId: string; start: string; resourceId: string | null }[];
  patients: Record<string, unknown>[];
}

export const managedDevicesApi = {
  /**
   * Enroll this browser (step-up + Idempotency-Key): a device key pair is generated locally, the public key is sent,
   * and the device-bound token comes back wrapped to it (RSA-OAEP-256). The token is unwrapped and stored locally.
   */
  request: async (siteId: string) => {
    const { pair, pem } = await newDeviceKeyPair();
    const d = await post<ManagedDevice & { wrappedToken?: string; keyAlgorithm?: string }>('/managed-devices', { siteId, publicKeyPem: pem }, { idempotent: true, stepUpLabel: 'Enroll this device for downtime packets' });
    const token = d.wrappedToken ? new TextDecoder().decode(await unwrap(pair.privateKey, d.wrappedToken)) : d.token;
    await saveDeviceKey(d.id, pair.privateKey);
    if (token) deviceStore.set({ id: d.id, token, siteId: d.siteId, expiresAt: d.expiresAt, status: d.status });
    return { ...d, wrappedToken: undefined, etag: devTag(d) };
  },
  /** The caller's own devices. */
  mine: async (p: ListParams = {}) => {
    const page = await get<Page<ManagedDevice>>('/managed-devices', { limit: 100, ...p });
    return { ...page, items: page.items.map((d) => ({ ...d, etag: devTag(d) })) };
  },
  /** Requests waiting for approval (managed_device.manage). */
  pending: async (p: ListParams = {}) => {
    const page = await get<Page<ManagedDevice>>('/managed-devices/review', { limit: 100, ...p });
    return { ...page, items: page.items.map((d) => ({ ...d, etag: devTag(d) })) };
  },
  /** Independent approval: the requester can never approve. If-Match + step-up + Idempotency-Key. */
  approve: async (id: string, etag: string | null | undefined) =>
    post<ManagedDevice>(`/managed-devices/${seg(id)}:approve`, {}, { ifMatch: etag, idempotent: true, stepUpLabel: 'Approve managed device' }),
  revoke: async (id: string, etag: string | null | undefined) => {
    const d = await post<ManagedDevice>(`/managed-devices/${seg(id)}:revoke`, {}, { ifMatch: etag, idempotent: true, stepUpLabel: 'Revoke managed device' });
    if (deviceStore.get()?.id === id) deviceStore.clear();
    return d;
  },
  /** Download a packet and decrypt it on this device (audited download; the key never leaves the device). */
  openPacket: async (packetId: string): Promise<OpenedPacket> => {
    const dev = deviceStore.get();
    const key = dev ? await loadDeviceKey(dev.id) : null;
    if (!dev || !key) throw new Error('This browser does not hold the device key for this enrollment.');
    const dl = await downtimeApi.download(packetId);
    if (!dl.wrappedKey) throw new Error('The packet key is not wrapped for this device.');
    const raw = await unwrap(key, dl.wrappedKey);
    const aes = await subtle().importKey('raw', raw, { name: 'AES-GCM' }, false, ['decrypt']);
    const data = b64ToBytes(dl.content);
    const iv = data.subarray(0, 12);
    const tag = data.subarray(12, 28);
    const body = new Uint8Array(data.length - 12);
    body.set(data.subarray(28), 0);
    body.set(tag, data.length - 28);
    const plain = await subtle().decrypt({ name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(dl.aad ?? ''), tagLength: 128 }, aes, body);
    return JSON.parse(new TextDecoder().decode(plain)) as OpenedPacket;
  },
};

export const downtimeApi = {
  /** Step-up + Idempotency-Key + approved managed device; siteId must be the device's site. */
  createPacket: (body: CreateDowntimePacketRequest) =>
    post<DowntimePacket>('/downtime-packets', body, { idempotent: true, stepUpLabel: 'Generate downtime packet', headers: deviceHeaders() }),
  packet: (id: string) => get<DowntimePacket>(`/downtime-packets/${seg(id)}`, undefined, { headers: deviceHeaders() }),
  /** Packets this actor created on this device. */
  packets: (p: ListParams & { siteId?: string } = {}) => get<Page<DowntimePacket>>('/downtime-packets', { ...p }, { headers: deviceHeaders() }),
  /** Encrypted file; every download is audited. */
  download: (id: string) => get<DowntimePacketDownload>(`/downtime-packets/${seg(id)}/download`, undefined, { headers: deviceHeaders() }),
  backEntries: (p: ListParams = {}) => get<Page<BackEntryResult>>('/back-entries', {...p}),
  backEntryRecord: (id: string) => get<BackEntryResult>(`/back-entries/${seg(id)}`),
  reconcileBackEntry: (id: string, decision: 'accepted' | 'rejected', resolutionCode: string, version: number) =>
    post<BackEntryResult>(`/back-entries/${seg(id)}:reconcile`, {decision,resolutionCode}, {ifMatch:etagFromRow(version),idempotent:true,stepUpLabel:'Independently review downtime entry'}),
  applyBackEntry: (id: string, version: number) =>
    post<BackEntryResult>(`/back-entries/${seg(id)}:apply`, {}, {ifMatch:etagFromRow(version),idempotent:true,purpose:'TREATMENT',stepUpLabel:'Apply reviewed downtime observation'}),
  backEntry: (body: BackEntryRequest) => post<BackEntryResult>('/back-entries', body, { idempotent: true }),
  safetyReport: (body: SafetyReportRequest) => post<{ id: string; reference: string }>('/safety-reports', body, { idempotent: true }),
};
